import { useEffect, useRef, useState } from "react";

/**
 * Visibility observation for a widget against a given scroll container
 * (root = the container element). For dashboard tiles in the v2 builder,
 * the root is the tile grid scroller — passed via a stable ref from the
 * dashboard. Without an explicit root, IntersectionObserver defaults to
 * the document viewport and off-screen tiles would still report
 * `isIntersecting: true` whenever their bbox happens to overlap the
 * window, defeating the fetch gate.
 *
 * Race-free: the parent's ref is read INSIDE this hook's useEffect
 * (after React's commit + ref.attach), so the very first observer is
 * built against the real container element. The alternative — bouncing
 * the root through `useState(root) → setRoot(<scroller>)` — creates a
 * window where the first observer sees `root: null` (window-level) and
 * reports `isIntersecting: true` for off-screen tiles whose bbox
 * overlaps the document viewport; enough for the fetch gate to trip
 * the kept-data invariant on tiles that should never have fetched.
 */
export const useCheckWidgetInViewport = <
  T extends HTMLElement = HTMLElement,
>(scrollContainerRef?: {
  current: Element | null;
}) => {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    // Read the parent's ref live — the ref is attached during React
    // commit, BEFORE any useEffect fires, so `scrollContainerRef.current`
    // is the actual container even on the very first effect run.
    const root = scrollContainerRef?.current ?? null;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.01, root },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [scrollContainerRef]);

  return { ref, inView };
};

/**
 * Re-entry refetch bridge: the polling interval in widget fetch hooks
 * keeps its schedule across viewport toggles (visibility only gates
 * ticks, not the interval). When a tile re-enters the viewport after
 * being scrolled out, this hook fires one immediate refetch so the
 * kept data is not stale.
 */
export const useRefetchWhenVisible = (
  inViewport: boolean,
  latestLoadRef: { current: (() => void) | null },
) => {
  const inViewportRef = useRef(inViewport);
  inViewportRef.current = inViewport;
  const prevInViewportRef = useRef(inViewport);

  useEffect(() => {
    const becameVisible = inViewport && !prevInViewportRef.current;
    prevInViewportRef.current = inViewport;
    if (becameVisible) latestLoadRef.current?.();
  }, [inViewport, latestLoadRef]);

  return inViewportRef;
};
